import { useState } from "react"
import {
  CustomSelectField,
  Field,
  FormError,
  PageHero,
  SectionLabel,
  SuccessCard,
  TextAreaField,
  usePublicForm,
} from "./components"
import { SchoolGrid } from "./school-cards"

const ROLES = ["Head of school", "Principal", "Proprietor", "Administrator", "Teacher", "Other"] as const

const LOCATIONS = [
  "e.g., Enugu",
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT Abuja",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
] as const

function PartnershipForm() {
  const { status, submit, reset } = usePublicForm("/api/public/applications")
  const [confirmedReset, setConfirmedReset] = useState(0)

  if (status === "success") {
    return (
      <SuccessCard
        eyebrow="Application received"
        title="Thank you for partnering with us."
        message="Your application is in. Our team will review it and reach out to your contact person to explore the partnership."
        onReset={() => {
          setConfirmedReset((n) => n + 1)
          reset()
        }}
      />
    )
  }

  return (
    <div className="partner-schools-form-card">
      <form
        key={confirmedReset}
        onSubmit={(event) => {
          const form = new FormData(event.currentTarget)
          const schoolName = String(form.get("schoolName") ?? "")
          const location = String(form.get("location") ?? "")
          const contactPerson = String(form.get("contactPerson") ?? "")
          const role = String(form.get("role") ?? "")
          const enrollment = String(form.get("enrollment") ?? "")
          const why = String(form.get("why") ?? "")
          const body = [
            `School: ${schoolName}`,
            `Location: ${location}`,
            `Contact: ${contactPerson} (${role})`,
            `Current student enrollment: ${enrollment}`,
            "",
            "Why partner with Redempta:",
            why,
          ].join("\n")
          submit(event, {
            type: "partner-school",
            applicantName: contactPerson,
            applicantEmail: String(form.get("contactEmail") ?? ""),
            organization: schoolName,
            body,
          })
        }}
        className="public-form partner-schools-form"
      >
        <Field label="School name" name="schoolName" required placeholder="Enter school name" disabled={status === "loading"} />
        <CustomSelectField label="Location (city, state)" name="location" options={LOCATIONS} disabled={status === "loading"} />
        <div className="partner-schools-form__contact-row grid grid-cols-2 gap-4 sm:gap-5">
          <Field label="Contact person" name="contactPerson" required placeholder="Enter name" disabled={status === "loading"} />
          <CustomSelectField label="Role" name="role" options={ROLES} disabled={status === "loading"} />
        </div>
        <Field label="Contact email" name="contactEmail" required type="email" placeholder="name@school.org" disabled={status === "loading"} />
        <Field label="Current student enrollment" name="enrollment" required type="number" min={1} placeholder="110" disabled={status === "loading"} />
        <TextAreaField
          label="Why partner with Redempta?"
          name="why"
          required
          placeholder="Tell us about your school's values, support for scholarship students, and long-term commitments..."
          disabled={status === "loading"}
        />
        <div className="partner-schools-form__actions">
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full rounded-full bg-forest-950 py-4 text-base font-semibold text-white transition-colors hover:bg-forest-800 disabled:opacity-60"
          >
            {status === "loading" ? "Sending..." : "Submit application"}
          </button>
          <p className="mt-4 text-center text-sm text-ink/60">
            We treat every application as a formal commitment to explore partnership, not a mailing list signup.
          </p>
          {status === "error" && <FormError />}
        </div>
      </form>
    </div>
  )
}

export function PartnerSchoolsPage() {
  return (
    <>
      <PageHero
        eyebrow="Partner Schools"
        eyebrowDot
        className="partner-schools-hero"
        title={
          <>
            Quality classrooms, <span className="partner-schools-hero__accent">committed partners.</span>
          </>
        }
        lede={
          <>
            We work with a small, carefully-chosen set of private schools <span className="partner-schools-hero__accent">willing to stand behind our scholars for the long term.</span>
          </>
        }
      />

      <section className="partner-schools-listing bg-cream-50 py-16 lg:pb-[30px] lg:pt-[60px]">
        <div className="public-container">
          <SchoolGrid />

          <div className="partner-schools-coming-soon">
            <div className="partner-schools-coming-soon__content">
              <h2>Your school here</h2>
              <p className="partner-schools-coming-soon__body">
                We&apos;re expanding our partner network slowly and intentionally. If your school shares our standards, apply below.
              </p>
            </div>
            <a
              href="#apply"
              className="partner-schools-coming-soon__link"
            >
              Apply to partner
            </a>
          </div>
        </div>
      </section>

      <section id="apply" className="partner-schools-application scroll-mt-24 bg-cream-50 pb-[7px] lg:pb-[100px] lg:pt-[30px]">
        <div className="public-container">
          <div className="partner-schools-application__intro mx-auto flex max-w-[926px] flex-col items-center text-center lg:h-[157px] lg:justify-between">
            <SectionLabel>Become a Partner School</SectionLabel>
            <h2>
              Partnership application
            </h2>
            <p>
              A detailed application is intentional — it helps us find schools truly committed to standing behind our scholars, not just casual inquirers.
            </p>
          </div>
          <div className="partner-schools-application__form mx-auto mt-8 lg:mt-[60px]">
            <PartnershipForm />
          </div>
        </div>
      </section>
    </>
  )
}
